import React, { useState, useEffect } from 'react';

const RegistrationDetailModal = ({ 
  isOpen, 
  onClose, 
  registration, 
  onApprove, 
  onReject, 
  onDelete,
  isProcessing 
}) => {
  const [adminRemarks, setAdminRemarks] = useState('');
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState({
    leaveEntitlement: '',
    officialEmail: '',
    dateOfJoining: '',
    designation: '',
    organogram: '',
    empID: '',
    phone: '',
    whatsApp: '',
    address: '',
    country: '',
    location: '',
    bio: ''
  });
  
  // Initialize edited fields when registration changes
  useEffect(() => {
    if (registration) {
      setEditedFields({
        leaveEntitlement: registration.leaveEntitlement || 21,
        officialEmail: registration.email || '',
        dateOfJoining: registration.dateOfJoining || '',
        designation: registration.designation || '',
        organogram: registration.organogram || '',
        empID: registration.empID || '',
        phone: registration.phone || '',
        whatsApp: registration.whatsApp || '',
        address: registration.address || '',
        country: registration.country || '',
        location: registration.location || '',
        bio: registration.bio || ''
      });
    }
  }, [registration]);
  
  if (!isOpen || !registration) return null;
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const handleClose = () => {
    setAdminRemarks('');
    setShowApprovalForm(false);
    setShowRejectionForm(false);
    setIsEditMode(false);
    onClose();
  };
  
  const handleFieldChange = (field, value) => {
    setEditedFields(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
  };
  
  const handleSaveChanges = () => {
    // Here you would typically call an API to update the registration
    // For now, we'll just log the changes
    console.log('Saving changes:', editedFields);
    setIsEditMode(false);
  };
  
  const handleApprove = () => {
    if (showApprovalForm) {
      onApprove(registration.id, adminRemarks);
      handleClose();
    } else {
      setShowApprovalForm(true);
    }
  };
  
  const handleReject = () => {
    if (showRejectionForm) {
      onReject(registration.id, adminRemarks);
      handleClose();
    } else {
      setShowRejectionForm(true);
    }
  };
  
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this registration? This action cannot be undone.')) {
      onDelete(registration.id);
      handleClose();
    }
  };
  
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-badge approved';
      case 'rejected':
        return 'status-badge rejected';
      case 'pending':
        return 'status-badge pending';
      default:
        return 'status-badge';
    }
  };
  
  return (
    <div className="registration-detail-modal">
      <div className="modal-overlay" onClick={handleClose}></div>
      <div className="modal-container">
        <div className="modal-header">
          <h3>Registration Details</h3>
          <div className="header-actions">
            {registration.status === 'pending' && (
              <button 
                className="edit-button" 
                onClick={handleEditToggle}
                disabled={isProcessing}
              >
                {isEditMode ? 'Cancel Edit' : 'Edit Details'}
              </button>
            )}
            <button className="close-button" onClick={handleClose}>
              &times;
            </button>
          </div>
        </div>
        
        <div className="modal-body">
          <div className="registration-details">
            <div className="detail-section">
              <h4>Profile Picture</h4>
              <div className="profile-picture-section">
                {registration.img ? (
                  <div className="profile-image-container">
                    <img 
                      src={registration.img} 
                      alt={`${registration.name} profile`}
                      className="profile-image"
                    />
                  </div>
                ) : (
                  <div className="no-image-placeholder">
                    <div className="placeholder-icon">👤</div>
                    <p>No profile picture uploaded</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Basic Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Full Name</label>
                  <p>{registration.name || 'Not provided'}</p>
                </div>
                <div className="detail-item">
                  <label>Employee ID</label>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedFields.empID}
                      onChange={(e) => handleFieldChange('empID', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <p>{registration.empID || 'Not provided'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Official Email</label>
                  {isEditMode ? (
                    <input
                      type="email"
                      value={editedFields.officialEmail}
                      onChange={(e) => handleFieldChange('officialEmail', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <p>{registration.email || 'Not provided'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Personal Email</label>
                  <p>{registration.personalEmail || 'Not provided'}</p>
                </div>
                <div className="detail-item">
                  <label>Designation</label>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedFields.designation}
                      onChange={(e) => handleFieldChange('designation', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <p>{registration.designation || 'Not provided'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Department</label>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedFields.organogram}
                      onChange={(e) => handleFieldChange('organogram', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <p>{registration.organogram || 'Not provided'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Date of Birth</label>
                  <p>{registration.dob ? new Date(registration.dob).toLocaleDateString() : 'Not provided'}</p>
                </div>
                <div className="detail-item">
                  <label>Date of Joining</label>
                  {isEditMode ? (
                    <input
                      type="date"
                      value={editedFields.dateOfJoining}
                      onChange={(e) => handleFieldChange('dateOfJoining', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <p>{registration.dateOfJoining ? new Date(registration.dateOfJoining).toLocaleDateString() : 'Not provided'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Leave Entitlement</label>
                  {isEditMode ? (
                    <input
                      type="number"
                      value={editedFields.leaveEntitlement}
                      onChange={(e) => handleFieldChange('leaveEntitlement', parseInt(e.target.value) || 21)}
                      className="edit-input"
                      min="1"
                      max="365"
                    />
                  ) : (
                    <p>{registration.leaveEntitlement || 21} days</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Contact Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Phone Number</label>
                  {isEditMode ? (
                    <input
                      type="tel"
                      value={editedFields.phone}
                      onChange={(e) => handleFieldChange('phone', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <p>{registration.phone || 'Not provided'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>WhatsApp</label>
                  {isEditMode ? (
                    <input
                      type="tel"
                      value={editedFields.whatsApp}
                      onChange={(e) => handleFieldChange('whatsApp', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <p>{registration.whatsApp || 'Not provided'}</p>
                  )}
                </div>
                <div className="detail-item full-width">
                  <label>Address</label>
                  {isEditMode ? (
                    <textarea
                      value={editedFields.address}
                      onChange={(e) => handleFieldChange('address', e.target.value)}
                      className="edit-textarea"
                      rows="3"
                    />
                  ) : (
                    <p>{registration.address || 'Not provided'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Country</label>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedFields.country}
                      onChange={(e) => handleFieldChange('country', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <p>{registration.country || 'Not provided'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Location</label>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedFields.location}
                      onChange={(e) => handleFieldChange('location', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <p>{registration.location || 'Not provided'}</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Emergency Contact</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Emergency Contact</label>
                  <p>{registration.emergencyContact || 'Not provided'}</p>
                </div>
                <div className="detail-item">
                  <label>Emergency Number</label>
                  <p>{registration.emergencyNumber || 'Not provided'}</p>
                </div>
                <div className="detail-item">
                  <label>Relationship</label>
                  <p>{registration.emergencyRelation || 'Not provided'}</p>
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Language Preferences</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Preferred Language</label>
                  <p>{registration.preferredLang || 'Not provided'}</p>
                </div>
                <div className="detail-item full-width">
                  <label>All Languages</label>
                  <p>{registration.allLang || 'Not provided'}</p>
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Personal Details</h4>
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <label>Bio / Personal Details</label>
                  {isEditMode ? (
                    <textarea
                      value={editedFields.bio || ''}
                      onChange={(e) => handleFieldChange('bio', e.target.value)}
                      className="edit-textarea"
                      rows="4"
                      placeholder="Enter personal details, background, interests..."
                    />
                  ) : (
                    <div className="bio-content">
                      {registration.bio ? (
                        <p>{registration.bio}</p>
                      ) : (
                        <p className="no-content">No personal details provided</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Registration Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Submitted At</label>
                  <p>{formatDate(registration.submittedAt)}</p>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <p>
                    <span className={getStatusBadgeClass(registration.status)}>
                      {registration.status}
                    </span>
                  </p>
                </div>
                {registration.approvedAt && (
                  <div className="detail-item">
                    <label>Approved At</label>
                    <p>{formatDate(registration.approvedAt)}</p>
                  </div>
                )}
                {registration.rejectedAt && (
                  <div className="detail-item">
                    <label>Rejected At</label>
                    <p>{formatDate(registration.rejectedAt)}</p>
                  </div>
                )}
                {registration.adminRemarks && (
                  <div className="detail-item full-width">
                    <label>Admin Remarks</label>
                    <p>{registration.adminRemarks}</p>
                  </div>
                )}
              </div>
            </div>
            
            {(showApprovalForm || showRejectionForm) && (
              <div className="form-section">
                <h4>{showApprovalForm ? 'Approval Remarks' : 'Rejection Remarks'}</h4>
                <div className="form-group">
                  <label htmlFor="adminRemarks">
                    {showApprovalForm ? 'Approval Remarks (Optional)' : 'Rejection Reason (Required)'}
                  </label>
                  <textarea
                    id="adminRemarks"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                    placeholder={showApprovalForm 
                      ? "Add any remarks for the approval..." 
                      : "Please provide a reason for rejection..."
                    }
                    rows={4}
                    required={showRejectionForm}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="modal-footer">
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isProcessing}
          >
            Close
          </button>
          
          {isEditMode && (
            <button
              className="save-button"
              onClick={handleSaveChanges}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <span className="spinner"></span>
              ) : 'Save Changes'}
            </button>
          )}
          
          {registration.status === 'pending' && !isEditMode && (
            <>
              <button
                className="approve-button"
                onClick={handleApprove}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <span className="spinner"></span>
                ) : showApprovalForm ? 'Confirm Approval' : 'Approve'}
              </button>
              
              <button
                className="reject-button"
                onClick={handleReject}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <span className="spinner"></span>
                ) : showRejectionForm ? 'Confirm Rejection' : 'Reject'}
              </button>
            </>
          )}
          
          <button
            className="delete-button"
            onClick={handleDelete}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className="spinner"></span>
            ) : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationDetailModal;
